import { lazy, Suspense } from "react";
import { Button, Card } from "../ui";
import { Loading } from "../ui/Loading";
import { handleMicrosoftSignIn } from "../../handlers/authHandlers";
import { env } from "../../config/env";
import { useMessage } from "../../hooks";
import type { LoginCardProps } from "../../types";

// Development-only component - lazy loaded and tree-shaken out in production
const DevBypassLogin = lazy(() =>
  import.meta.env.MODE === "development"
    ? import("./DevBypassLogin")
    : Promise.resolve({ default: () => null }),
);

export function LoginCard({
  msal,
  isLoading,
  setIsLoading,
  isProcessingRedirect = false,
  authComplete,
  suspended = false,
  loginMicrosoft,
}: LoginCardProps) {
  const message = useMessage();

  return (
    <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg relative z-10">
      <Card className="shadow-logincard bg-white mx-auto">
        <div className="px-4 py-6 sm:px-6 md:px-8 md:py-8 space-y-6 sm:space-y-8">
          <div className="flex items-center justify-center">
            <img
              src="logo.svg"
              alt="serviceX logo"
              className="h-4 sm:h-6 md:h-[30px] w-auto"
            />
          </div>

          {suspended && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <span className="text-red-500 mt-0.5 text-base leading-none">
                ⚠
              </span>
              <div>
                <p className="text-sm font-semibold text-red-700">
                  Account Suspended
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  Your account has been suspended. Please contact your
                  administrator.
                </p>
              </div>
            </div>
          )}

          {isProcessingRedirect ? (
            <div className="text-center space-y-6 py-8">
              <Loading />
              <div className="space-y-2">
                <p className="text-sm sm:text-base font-medium text-customBlack">
                  Processing authentication...
                </p>
                <p className="text-xs sm:text-sm text-grey">
                  Please wait while we complete your sign in
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center space-y-2">
                <p className="text-xs sm:text-sm text-grey">
                  Sign in with your Microsoft account to proceed
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  type="button"
                  onClick={() =>
                    handleMicrosoftSignIn(msal, setIsLoading, message)
                  }
                  disabled={isLoading || msal.inProgress !== "none"}
                  fullWidth
                  size="lg"
                  className="h-10 sm:h-12 md:h-14 bg-white hover:bg-gray-50 text-customBlack border-2 border-bordercolor hover:border-primary transition-all duration-200 font-medium rounded-md flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading || msal.inProgress !== "none" ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                  ) : (
                    <img src="microsoft.svg" alt="Microsoft logo" />
                  )}
                  <span className="truncate">
                    {isLoading || msal.inProgress !== "none"
                      ? "Redirecting to Microsoft..."
                      : "Sign in with Microsoft"}
                  </span>
                </Button>

                {env.isDevelopment && (
                  <Suspense fallback={null}>
                    <DevBypassLogin
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                      loginMicrosoft={loginMicrosoft}
                      authComplete={authComplete}
                    />
                  </Suspense>
                )}

                <div className="text-center px-2">
                  <p className="text-xs text-grey leading-relaxed">
                    By signing in, you agree to our{" "}
                    <button className="text-primary hover:underline">
                      Terms of Service
                    </button>{" "}
                    and{" "}
                    <button className="text-primary hover:underline">
                      Privacy Policy
                    </button>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default LoginCard;
